import { TradingHour } from 'proto/trade-copier_pb';

// Trading hours are stored in UTC as (dayOfWeek, secondsOfDay) segments,
// dayOfWeek per the proto enum: 0 = Monday ... 6 = Sunday. Local ranges that
// cross UTC midnight are split on save and re-merged for display.

export type DayOfWeekNumber = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type LocalTradingHour = {
  dayOfWeek: DayOfWeekNumber;
  startTime: string; // HH:MM local time
  endTime: string;
};

export const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

export const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const MS_PER_MINUTE = 60000;
const DAYS_PER_WEEK = 7;

export const dayLabel = (day: number): string => DAY_LABELS[day] ?? 'Monday';

export const dayNumber = (label: string): DayOfWeekNumber => {
  const index = DAY_LABELS.indexOf(label as (typeof DAY_LABELS)[number]);
  return (index >= 0 ? index : 0) as DayOfWeekNumber;
};

export const getTimezoneOffsetMinutes = (timezone: string): number => {
  if (!timezone || timezone === 'UTC') {
    return 0;
  }
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(now);
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
    // formatToParts instead of parsing locale date strings, which is unreliable across browsers
    const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return Math.round((asUTC - now.getTime()) / MS_PER_MINUTE);
  } catch {
    return 0;
  }
};

export const getTimezoneOffsetSeconds = (timezone: string): number => getTimezoneOffsetMinutes(timezone) * SECONDS_PER_MINUTE;

export const normalizeTime = (timeSec: number) => ((timeSec % SECONDS_PER_DAY) + SECONDS_PER_DAY) % SECONDS_PER_DAY;

const adjustDay = (day: number, seconds: number) => (day + Math.floor(seconds / SECONDS_PER_DAY) + DAYS_PER_WEEK) % DAYS_PER_WEEK;

export const timeStringToSeconds = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * SECONDS_PER_HOUR + m * SECONDS_PER_MINUTE;
};

export const toHHMM = (sec: number) => {
  // end of day renders as 23:59 — '00:00' would fail start < end validation
  const capped = sec >= SECONDS_PER_DAY ? SECONDS_PER_DAY - SECONDS_PER_MINUTE : sec;
  const h = Math.floor(capped / SECONDS_PER_HOUR);
  const m = Math.floor((capped % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const splitAtDayBoundary = (sDay: number, eDay: number, start: number, end: number) => {
  const segments =
    sDay === eDay && start < end
      ? [{ day: sDay, start, end }]
      : [
          { day: sDay, start, end: SECONDS_PER_DAY },
          { day: (sDay + 1) % DAYS_PER_WEEK, start: 0, end }
        ];
  return segments.filter((seg) => seg.start !== seg.end);
};

export const localToUTCTradingHours = (hours: LocalTradingHour[], timezone: string): TradingHour.AsObject[] => {
  const offsetSec = getTimezoneOffsetSeconds(timezone);
  const result: TradingHour.AsObject[] = [];

  for (const hour of hours) {
    const sUTC = timeStringToSeconds(hour.startTime) - offsetSec;
    const eUTC = timeStringToSeconds(hour.endTime) - offsetSec;
    const segments = splitAtDayBoundary(
      adjustDay(hour.dayOfWeek, sUTC),
      adjustDay(hour.dayOfWeek, eUTC),
      normalizeTime(sUTC),
      normalizeTime(eUTC)
    );
    result.push(...segments.map((seg) => ({ dayOfWeek: seg.day as DayOfWeekNumber, startTime: seg.start, endTime: seg.end })));
  }

  return result;
};

export const utcToLocalTradingHours = (utcHours: TradingHour.AsObject[], timezone: string): LocalTradingHour[] => {
  const offsetSec = getTimezoneOffsetSeconds(timezone);
  const byDay = new Map<number, { start: number; end: number }[]>();

  for (const utc of utcHours) {
    const sLocal = utc.startTime + offsetSec;
    const eLocal = utc.endTime + offsetSec;
    const segments = splitAtDayBoundary(
      adjustDay(utc.dayOfWeek, sLocal),
      adjustDay(utc.dayOfWeek, eLocal),
      normalizeTime(sLocal),
      normalizeTime(eLocal)
    );
    for (const seg of segments) {
      if (!byDay.has(seg.day)) byDay.set(seg.day, []);
      byDay.get(seg.day)!.push({ start: seg.start, end: seg.end });
    }
  }

  // sort, then merge adjacent/overlapping ranges back into what the user entered
  const result: LocalTradingHour[] = [];
  Array.from(byDay.keys())
    .sort((a, b) => a - b)
    .forEach((day) => {
      const ranges = byDay.get(day)!.sort((a, b) => a.start - b.start);
      const merged: { start: number; end: number }[] = [];
      for (const range of ranges) {
        const last = merged[merged.length - 1];
        if (last && range.start <= last.end) {
          last.end = Math.max(last.end, range.end);
        } else {
          merged.push({ ...range });
        }
      }
      for (const range of merged) {
        result.push({ dayOfWeek: day as DayOfWeekNumber, startTime: toHHMM(range.start), endTime: toHHMM(range.end) });
      }
    });

  return result;
};

// Returns an error message, or null when valid.
export const validateLocalTradingHours = (hours: LocalTradingHour[]): string | null => {
  const rangesByDay: Record<number, { start: number; end: number }[]> = {};

  for (const hour of hours) {
    if (hour.dayOfWeek === undefined || !hour.startTime || !hour.endTime) {
      return 'Day, start time, and end time are required for all trading hours.';
    }

    const start = timeStringToSeconds(hour.startTime);
    const end = timeStringToSeconds(hour.endTime);
    if (start >= end) {
      return `End time must be after start time on ${dayLabel(hour.dayOfWeek)}.`;
    }

    for (const existing of rangesByDay[hour.dayOfWeek] || []) {
      if (Math.max(existing.start, start) < Math.min(existing.end, end)) {
        return `Overlapping trading hours detected on ${dayLabel(hour.dayOfWeek)}.`;
      }
    }
    rangesByDay[hour.dayOfWeek] = [...(rangesByDay[hour.dayOfWeek] || []), { start, end }];
  }

  return null;
};
